// CLASSIC (M14) ad templates, replaced by the M20 families (families.ts) after the owner found them cheap-looking
// (2026-10-12). Kept in code, not shown in the picker: an ad saved from one still opens and edits. 33 layouts over the owner's list — Instagram post 1:1 and 4:5, story 9:16,
// delivery-app banner, menu board 16:9 and vertical, printed menu page, daily special, promo/discount flyer,
// combo, new dish, happy hour. Every one has a photo slot, the dish name / description / price in Spanish
// and English side by side, the logo and the brand colours. All art is drawn from shapes and the bundled
// OFL fonts; nothing is copied from anyone's design.
import type { Builder, TemplateDef } from './dsl';

const bi = (es: string, en: string) => ({ es, en });

/** Name (ES big, EN under it) + description (ES, EN). Returns the bottom y. */
function dish(
  b: Builder,
  x: number,
  y: number,
  w: number,
  s: number,
  o: {
    color?: Parameters<Builder['text']>[4]['color'];
    align?: 'left' | 'center' | 'right';
    desc?: boolean;
    lines?: number;
  } = {},
) {
  // Box heights: a long Spanish name wraps to 2 lines and shrinks to fit; English and the English
  // description stay on one line (about 4 x s in all).
  const color = o.color ?? 'text';
  let yy = b.bi(x, y, w, 'dishName', {
    size: s,
    color,
    align: o.align,
    maxLines: o.lines ?? 2,
    esH: Math.round(s * 1.6),
    enSize: Math.round(s * 0.5),
    enH: Math.round(s * 0.62),
  });
  if (o.desc !== false)
    yy = b.bi(x, yy + s * 0.22, w, 'description', {
      font: 'body',
      weight: 400,
      size: Math.round(s * 0.42),
      color,
      align: o.align,
      maxLines: 2,
      esH: Math.round(s * 1.02),
      enSize: Math.round(s * 0.36),
      enH: Math.round(s * 0.45),
    });
  return yy;
}

/** A menu row: Spanish name (bold) + English (small) on the left, dotted leader, price on the right. */
function menuRow(
  b: Builder,
  i: number,
  x: number,
  y: number,
  w: number,
  s: number,
  color: Parameters<Builder['text']>[4]['color'] = 'text',
  lead: Parameters<Builder['line']>[4] = ['text', 0.35],
) {
  const pw = s * 4.2;
  b.text(x, y, w - pw - s, s * 1.2, {
    role: 'itemName',
    item: i,
    lang: 'es',
    size: s,
    color,
    maxLines: 1,
    weight: 700,
  });
  b.text(x, y + s * 1.2, w - pw - s, s * 0.95, {
    role: 'itemName',
    item: i,
    lang: 'en',
    font: 'body',
    size: s * 0.72,
    color,
    maxLines: 1,
    opacity: 0.8,
  });
  b.line(x + w * 0.55, y + s * 0.95, x + w - pw - s * 0.3, y + s * 0.95, lead, Math.max(1.5, s * 0.09), [
    0.1,
    s * 0.32,
  ]);
  b.text(x + w - pw, y, pw, s * 1.2, {
    role: 'itemPrice',
    item: i,
    size: s,
    color,
    align: 'right',
    maxLines: 1,
    weight: 800,
  });
}

/** A two-word label as one free text (e.g. "NUEVO · NEW"). */
const label = (
  b: Builder,
  x: number,
  y: number,
  w: number,
  h: number,
  text: string,
  size: number,
  color: Parameters<Builder['text']>[4]['color'],
  align: 'left' | 'center' | 'right' = 'left',
) =>
  b.text(x, y, w, h, {
    role: 'free',
    text,
    size,
    color,
    align,
    valign: 'middle',
    caps: true,
    tracking: 80,
    maxLines: 1,
  });

const CLASSIC: TemplateDef[] = [
  // ───────────── Instagram post 1:1 ─────────────
  {
    id: 'post-band',
    category: 'post',
    format: 'ig-square',
    name: bi('Foto con franja', 'Photo with band'),
    build: (b) => {
      b.photo(0, 0, 1080, 1080);
      b.rect(0, 740, 1080, 340, { fill: ['primary', 0.93] });
      b.logo(40, 40, 200, 120, { fill: ['white', 0.9], r: 18 });
      dish(b, 60, 770, 700, 66, { color: 'white' });
      b.priceBadge(915, 735, 125, { fill: 'secondary' });
    },
  },
  {
    id: 'post-cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Platillo recortado sobre color', 'Cut-out dish on colour'),
    build: (b) => {
      b.bg('primary');
      b.ellipse(540, 600, 470, 470, { fill: ['white', 0.12] });
      b.photo(140, 270, 800, 610, { fit: 'contain' });
      b.text(60, 36, 960, 100, {
        role: 'dishName',
        lang: 'es',
        size: 80,
        color: 'white',
        align: 'center',
        maxLines: 1,
      });
      b.text(60, 136, 960, 48, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 36,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.85,
      });
      b.text(60, 186, 960, 40, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 28,
        color: 'white',
        align: 'center',
        maxLines: 1,
      });
      b.text(60, 226, 960, 34, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 22,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.8,
      });
      b.rect(330, 900, 420, 120, { fill: 'secondary', r: 60 });
      b.text(330, 900, 420, 120, { role: 'price', size: 64, align: 'center', valign: 'middle', maxLines: 1 });
      b.logo(40, 960, 160, 90);
    },
  },
  {
    id: 'post-split',
    category: 'post',
    format: 'ig-square',
    name: bi('Mitad foto, mitad texto', 'Half photo, half text'),
    build: (b) => {
      b.bg('background');
      b.photo(0, 0, 540, 1080);
      b.logo(600, 60, 220, 120);
      const y = dish(b, 600, 260, 420, 62);
      b.line(600, y + 30, 760, y + 30, 'primary', 6);
      b.text(600, y + 60, 420, 110, { role: 'price', size: 86, color: 'primary', maxLines: 1 });
      b.text(600, 960, 420, 60, {
        role: 'contact',
        font: 'body',
        size: 26,
        color: 'text',
        maxLines: 2,
        opacity: 0.8,
      });
    },
  },
  {
    id: 'post-frame',
    category: 'post',
    format: 'ig-square',
    name: bi('Marco minimalista', 'Minimal frame'),
    build: (b) => {
      b.bg('white');
      b.rect(40, 40, 1000, 1000, { stroke: 'text', strokeW: 3 });
      b.photo(90, 90, 900, 640, { shape: 'rounded', r: 24 });
      b.text(90, 770, 680, 80, { role: 'dishName', lang: 'es', size: 58, maxLines: 1 });
      b.text(90, 850, 680, 50, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        size: 32,
        maxLines: 1,
        opacity: 0.75,
      });
      b.text(90, 905, 680, 60, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 26,
        maxLines: 2,
        opacity: 0.85,
      });
      b.text(90, 967, 680, 32, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 21,
        maxLines: 1,
        opacity: 0.7,
      });
      b.text(760, 780, 230, 110, { role: 'price', size: 60, color: 'primary', align: 'right', maxLines: 1 });
      b.logo(830, 930, 160, 80);
    },
  },
  {
    id: 'post-circle',
    category: 'post',
    format: 'ig-square',
    name: bi('Platillo en círculo', 'Dish in a circle'),
    build: (b) => {
      b.bg('secondary');
      b.rect(0, 0, 1080, 300, { fill: 'primary' });
      b.photo(240, 160, 600, 600, { shape: 'ellipse', stroke: 'white', strokeW: 16 });
      b.logo(40, 40, 180, 100);
      dish(b, 80, 800, 920, 60, { align: 'center' });
      b.priceBadge(880, 230, 110, { fill: 'white', color: 'primary' });
    },
  },
  // ───────────── Instagram post 4:5 ─────────────
  {
    id: 'portrait-classic',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Vertical clásico', 'Classic portrait'),
    build: (b) => {
      b.bg('background');
      b.photo(0, 0, 1080, 860);
      b.logo(40, 40, 200, 120, { fill: ['white', 0.9], r: 16 });
      b.rect(0, 860, 1080, 12, { fill: 'primary' });
      const y = dish(b, 60, 910, 700, 64);
      b.text(760, 905, 280, 110, { role: 'price', size: 72, color: 'primary', align: 'right', maxLines: 1 });
      b.text(60, Math.max(y + 20, 1250), 960, 50, {
        role: 'contact',
        font: 'body',
        size: 26,
        maxLines: 1,
        opacity: 0.7,
      });
    },
  },
  {
    id: 'portrait-bold',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Titular grande', 'Big headline'),
    ad: { headline: bi('¡Para chuparse los dedos!', 'Finger-licking good!') },
    build: (b) => {
      b.bg('primary');
      b.text(60, 60, 960, 190, {
        role: 'headline',
        lang: 'es',
        size: 92,
        color: 'white',
        caps: true,
        maxLines: 2,
        leading: 1,
      });
      b.text(60, 250, 960, 50, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 36,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.photo(60, 330, 960, 700, { shape: 'rounded', r: 40 });
      dish(b, 60, 1060, 680, 54, { color: 'white' });
      b.priceBadge(905, 1150, 115, { fill: 'secondary' });
      b.logo(840, 1280, 160, 56);
    },
  },
  {
    id: 'portrait-card',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Tarjeta flotante', 'Floating card'),
    build: (b) => {
      b.photo(0, 0, 1080, 1350);
      b.rect(60, 900, 960, 390, { fill: ['white', 0.95], r: 36 });
      dish(b, 110, 935, 620, 56);
      b.text(730, 940, 250, 100, { role: 'price', size: 64, color: 'primary', align: 'right', maxLines: 1 });
      b.logo(780, 1190, 200, 80);
    },
  },
  // ───────────── Story 9:16 ─────────────
  {
    id: 'story-top',
    category: 'story',
    format: 'story',
    name: bi('Foto arriba', 'Photo on top'),
    build: (b) => {
      b.bg('background');
      b.photo(0, 0, 1080, 1150);
      b.logo(40, 60, 220, 130, { fill: ['white', 0.9], r: 18 });
      b.rect(0, 1150, 1080, 16, { fill: 'primary' });
      const y = dish(b, 70, 1220, 940, 80, { align: 'center' });
      b.rect(290, Math.max(y + 50, 1640), 500, 150, { fill: 'primary', r: 75 });
      b.text(290, Math.max(y + 50, 1640), 500, 150, {
        role: 'price',
        size: 80,
        color: 'white',
        align: 'center',
        valign: 'middle',
        maxLines: 1,
      });
    },
  },
  {
    id: 'story-cutout',
    category: 'story',
    format: 'story',
    name: bi('Recorte centrado', 'Centred cut-out'),
    ad: { tagline: bi('Pida por mensaje', 'Order by message') },
    build: (b) => {
      b.bg('primary');
      b.ellipse(540, 930, 520, 520, { fill: ['secondary', 0.9] });
      b.photo(90, 540, 900, 800, { fit: 'contain' });
      b.text(60, 140, 960, 220, {
        role: 'dishName',
        lang: 'es',
        size: 104,
        color: 'white',
        align: 'center',
        maxLines: 2,
        leading: 1.02,
      });
      b.text(60, 370, 960, 60, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 42,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.85,
      });
      b.text(60, 436, 960, 44, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 34,
        color: 'white',
        align: 'center',
        maxLines: 1,
      });
      b.text(60, 480, 960, 36, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 26,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.8,
      });
      b.priceBadge(820, 1420, 150, { fill: 'white', color: 'primary', star: true });
      b.text(60, 1640, 960, 70, {
        role: 'tagline',
        lang: 'es',
        size: 54,
        color: 'white',
        align: 'center',
        maxLines: 1,
      });
      b.text(60, 1712, 960, 50, {
        role: 'tagline',
        lang: 'en',
        font: 'body',
        size: 34,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.8,
      });
      b.logo(400, 1790, 280, 100);
    },
  },
  {
    id: 'story-full',
    category: 'story',
    format: 'story',
    name: bi('Foto completa', 'Full-bleed photo'),
    build: (b) => {
      b.photo(0, 0, 1080, 1920);
      b.rect(0, 1260, 1080, 660, { fill: ['black', 0.55] });
      b.logo(40, 60, 220, 130);
      dish(b, 70, 1300, 940, 78, { color: 'white' });
      b.rect(70, 1740, 420, 130, { fill: 'secondary', r: 24 });
      b.text(70, 1740, 420, 130, { role: 'price', size: 72, align: 'center', valign: 'middle', maxLines: 1 });
    },
  },
  {
    id: 'story-stack',
    category: 'story',
    format: 'story',
    name: bi('Bloques de color', 'Colour blocks'),
    ad: { headline: bi('Hoy se antoja', 'Craving this today') },
    build: (b) => {
      b.bg('secondary');
      b.rect(0, 0, 1080, 520, { fill: 'primary' });
      b.text(70, 120, 940, 170, {
        role: 'headline',
        lang: 'es',
        size: 96,
        color: 'white',
        caps: true,
        maxLines: 2,
        leading: 1,
      });
      b.text(70, 300, 940, 60, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 40,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.photo(70, 420, 940, 940, { shape: 'rounded', r: 48, stroke: 'white', strokeW: 14 });
      dish(b, 70, 1420, 680, 62);
      b.priceBadge(890, 1520, 130, { fill: 'primary', color: 'white' });
      b.logo(70, 1780, 220, 100);
    },
  },
  // ───────────── Delivery-app banner ─────────────
  {
    id: 'delivery-left',
    category: 'delivery',
    format: 'delivery',
    name: bi('Foto a la izquierda', 'Photo on the left'),
    build: (b) => {
      b.bg('background');
      b.photo(0, 0, 640, 800);
      b.logo(690, 50, 220, 110);
      const y = dish(b, 690, 200, 460, 56);
      b.text(690, Math.max(y + 30, 560), 460, 110, {
        role: 'price',
        size: 80,
        color: 'primary',
        maxLines: 1,
      });
    },
  },
  {
    id: 'delivery-cutout',
    category: 'delivery',
    format: 'delivery',
    name: bi('Recorte sobre color', 'Cut-out on colour'),
    build: (b) => {
      b.bg('primary');
      b.ellipse(870, 400, 360, 360, { fill: ['white', 0.15] });
      b.photo(540, 60, 640, 680, { fit: 'contain' });
      b.text(60, 140, 520, 200, {
        role: 'dishName',
        lang: 'es',
        size: 70,
        color: 'white',
        maxLines: 2,
        leading: 1.02,
      });
      b.text(60, 345, 520, 50, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 34,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.text(60, 400, 520, 70, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 26,
        color: 'white',
        maxLines: 2,
      });
      b.text(60, 470, 520, 32, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 21,
        color: 'white',
        maxLines: 1,
        opacity: 0.8,
      });
      b.rect(60, 520, 330, 110, { fill: 'secondary', r: 55 });
      b.text(60, 520, 330, 110, { role: 'price', size: 58, align: 'center', valign: 'middle', maxLines: 1 });
      b.logo(60, 670, 200, 100);
    },
  },
  {
    id: 'delivery-white',
    category: 'delivery',
    format: 'delivery',
    name: bi('Platillo sobre blanco', 'Dish on white'),
    build: (b) => {
      b.bg('white');
      b.photo(300, 60, 600, 560, { fit: 'contain' });
      b.text(40, 630, 860, 70, { role: 'dishName', lang: 'es', size: 52, maxLines: 1 });
      b.text(40, 696, 860, 40, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        size: 28,
        maxLines: 1,
        opacity: 0.75,
      });
      b.text(40, 738, 860, 28, { role: 'description', lang: 'es', font: 'body', size: 20, maxLines: 1 });
      b.text(40, 766, 860, 26, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 17,
        maxLines: 1,
        opacity: 0.7,
      });
      b.text(900, 640, 260, 90, { role: 'price', size: 60, color: 'primary', align: 'right', maxLines: 1 });
      b.logo(1000, 30, 170, 90);
    },
  },
  // ───────────── Menu board 16:9 ─────────────
  {
    id: 'board-feature',
    category: 'board',
    format: 'board-h',
    name: bi('Platillo destacado + lista', 'Featured dish + list'),
    build: (b) => {
      b.bg('text');
      b.photo(0, 0, 820, 1080);
      b.rect(0, 820, 820, 260, { fill: ['primary', 0.92] });
      b.text(40, 836, 560, 96, {
        role: 'dishName',
        lang: 'es',
        size: 64,
        color: 'white',
        maxLines: 2,
        min: 26,
      });
      b.text(40, 930, 560, 50, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        size: 34,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.text(40, 984, 760, 40, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 26,
        color: 'white',
        maxLines: 1,
      });
      b.text(40, 1024, 760, 34, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 21,
        color: 'white',
        maxLines: 1,
        opacity: 0.8,
      });
      b.text(600, 850, 200, 100, {
        role: 'price',
        size: 64,
        color: 'secondary',
        align: 'right',
        maxLines: 1,
      });
      b.logo(880, 50, 260, 130);
      b.text(1180, 70, 700, 90, {
        role: 'headline',
        lang: 'es',
        size: 70,
        color: 'secondary',
        caps: true,
        maxLines: 1,
      });
      b.text(1180, 160, 700, 50, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 34,
        color: 'white',
        maxLines: 1,
        opacity: 0.8,
      });
      for (let i = 0; i < 6; i++) menuRow(b, i, 880, 260 + i * 130, 1000, 44, 'white', ['white', 0.35]);
    },
    ad: { headline: bi('Nuestro menú', 'Our menu') },
  },
  {
    id: 'board-columns',
    category: 'board',
    format: 'board-h',
    name: bi('Dos columnas', 'Two columns'),
    ad: { headline: bi('Menú del día', 'Menu of the day') },
    build: (b) => {
      b.bg('background');
      b.rect(0, 0, 1920, 170, { fill: 'primary' });
      b.text(60, 25, 1200, 90, {
        role: 'headline',
        lang: 'es',
        size: 76,
        color: 'white',
        caps: true,
        maxLines: 1,
      });
      b.text(60, 112, 1200, 48, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 34,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.logo(1620, 25, 260, 120);
      b.photo(60, 210, 560, 520, { shape: 'rounded', r: 32 });
      dish(b, 60, 750, 560, 44);
      b.text(60, 940, 560, 80, { role: 'price', size: 60, color: 'primary', maxLines: 1 });
      for (let i = 0; i < 8; i++) menuRow(b, i, 680 + (i % 2) * 620, 240 + Math.floor(i / 2) * 200, 560, 46);
    },
  },
  // ───────────── Menu board vertical ─────────────
  {
    id: 'boardv-list',
    category: 'board',
    format: 'board-v',
    name: bi('Vertical con lista', 'Vertical with list'),
    ad: { headline: bi('Menú', 'Menu') },
    build: (b) => {
      b.bg('background');
      b.photo(0, 0, 1080, 760);
      b.logo(40, 40, 240, 130, { fill: ['white', 0.9], r: 18 });
      b.rect(0, 540, 1080, 220, { fill: ['black', 0.55] });
      b.text(50, 555, 760, 70, { role: 'dishName', lang: 'es', size: 58, color: 'white', maxLines: 1 });
      b.text(50, 625, 760, 40, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        size: 32,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.text(50, 668, 760, 36, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 26,
        color: 'white',
        maxLines: 1,
      });
      b.text(50, 706, 760, 32, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 22,
        color: 'white',
        maxLines: 1,
        opacity: 0.8,
      });
      b.text(800, 560, 240, 90, { role: 'price', size: 64, color: 'secondary', align: 'right', maxLines: 1 });
      b.rect(0, 760, 1080, 150, { fill: 'primary' });
      b.text(60, 780, 960, 80, {
        role: 'headline',
        lang: 'es',
        size: 72,
        color: 'white',
        caps: true,
        align: 'center',
        maxLines: 1,
      });
      b.text(60, 858, 960, 44, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 32,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.85,
      });
      for (let i = 0; i < 6; i++) menuRow(b, i, 70, 960 + i * 150, 940, 48);
    },
  },
  {
    id: 'boardv-feature',
    category: 'board',
    format: 'board-v',
    name: bi('Vertical un platillo', 'Vertical single dish'),
    build: (b) => {
      b.bg('text');
      b.logo(400, 60, 280, 140);
      b.photo(60, 240, 960, 960, { shape: 'ellipse', stroke: 'secondary', strokeW: 18 });
      dish(b, 60, 1260, 960, 84, { color: 'white', align: 'center' });
      b.priceBadge(540, 1740, 130, { fill: 'secondary' });
    },
  },
  // ───────────── Printed menu page (Letter) ─────────────
  {
    id: 'menu-classic',
    category: 'menu',
    format: 'letter',
    name: bi('Menú clásico', 'Classic menu'),
    ad: { headline: bi('Menú', 'Menu') },
    build: (b) => {
      b.bg('white');
      b.logo(226, 36, 160, 70);
      b.text(36, 110, 540, 44, {
        role: 'headline',
        lang: 'es',
        size: 36,
        align: 'center',
        caps: true,
        tracking: 120,
        maxLines: 1,
      });
      b.text(36, 152, 540, 22, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 15,
        align: 'center',
        maxLines: 1,
        opacity: 0.7,
      });
      b.line(206, 184, 406, 184, 'primary', 2);
      b.photo(36, 196, 540, 196, { shape: 'rounded', r: 10 });
      b.rect(36, 326, 540, 66, { fill: ['white', 0.92] });
      b.text(48, 330, 400, 20, { role: 'dishName', lang: 'es', size: 16, maxLines: 1 });
      b.text(48, 349, 400, 13, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        size: 10.5,
        maxLines: 1,
        opacity: 0.75,
      });
      b.text(48, 362, 400, 13, { role: 'description', lang: 'es', font: 'body', size: 9.5, maxLines: 1 });
      b.text(48, 375, 400, 12, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 8.5,
        maxLines: 1,
        opacity: 0.7,
      });
      b.text(450, 336, 116, 28, { role: 'price', size: 20, color: 'primary', align: 'right', maxLines: 1 });
      for (let i = 0; i < 8; i++) menuRow(b, i, 54, 410 + i * 44, 504, 15);
      b.text(36, 760, 540, 18, {
        role: 'contact',
        font: 'body',
        size: 10,
        align: 'center',
        maxLines: 1,
        opacity: 0.7,
      });
    },
  },
  {
    id: 'menu-sidebar',
    category: 'menu',
    format: 'letter',
    name: bi('Menú con franja lateral', 'Menu with sidebar'),
    ad: { headline: bi('Nuestros platillos', 'Our dishes') },
    build: (b) => {
      b.bg('background');
      b.rect(-9, -9, 210, 810, { fill: 'primary' });
      b.logo(30, 40, 140, 80);
      b.photo(30, 150, 140, 140, { shape: 'ellipse', stroke: 'white', strokeW: 4 });
      b.text(30, 320, 140, 120, { role: 'dishName', lang: 'es', size: 18, color: 'white', maxLines: 4 });
      b.text(30, 440, 140, 60, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        size: 11,
        color: 'white',
        maxLines: 3,
        opacity: 0.85,
      });
      b.text(30, 510, 140, 36, { role: 'price', size: 22, color: 'secondary', maxLines: 1 });
      b.text(30, 552, 140, 60, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 10,
        color: 'white',
        maxLines: 4,
      });
      b.text(30, 616, 140, 50, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 8.5,
        color: 'white',
        maxLines: 4,
        opacity: 0.8,
      });
      b.text(230, 50, 350, 40, {
        role: 'headline',
        lang: 'es',
        size: 30,
        color: 'primary',
        caps: true,
        maxLines: 1,
      });
      b.text(230, 88, 350, 22, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 14,
        maxLines: 1,
        opacity: 0.7,
      });
      for (let i = 0; i < 8; i++) menuRow(b, i, 230, 140 + i * 72, 346, 15);
      b.text(230, 740, 350, 20, { role: 'contact', font: 'body', size: 10, maxLines: 1, opacity: 0.7 });
    },
  },
  // ───────────── Daily special ─────────────
  {
    id: 'special-chalk',
    category: 'special',
    format: 'ig-portrait',
    name: bi('Pizarra', 'Chalkboard'),
    ad: { headline: bi('Especial del día', "Today's special") },
    build: (b) => {
      b.bg('#26302b');
      b.rect(30, 30, 1020, 1290, { stroke: ['white', 0.6], strokeW: 4, r: 20 });
      b.text(60, 70, 960, 100, {
        role: 'headline',
        lang: 'es',
        size: 80,
        color: 'white',
        align: 'center',
        maxLines: 1,
      });
      b.text(60, 170, 960, 50, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 34,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.75,
      });
      b.photo(140, 250, 800, 640, { shape: 'rounded', r: 24 });
      dish(b, 80, 920, 920, 58, { color: 'white', align: 'center' });
      b.text(340, 1190, 400, 100, {
        role: 'price',
        size: 70,
        color: 'secondary',
        align: 'center',
        maxLines: 1,
      });
      b.logo(70, 1220, 180, 80);
    },
  },
  {
    id: 'special-ribbon',
    category: 'special',
    format: 'ig-square',
    name: bi('Cinta "Hoy"', '"Today" ribbon'),
    ad: { headline: bi('Hoy', 'Today'), tagline: bi('Solo hoy', 'Today only') },
    build: (b) => {
      b.photo(0, 0, 1080, 1080);
      b.rect(0, 70, 420, 110, { fill: 'primary' });
      b.text(40, 70, 360, 110, {
        role: 'headline',
        lang: 'es',
        size: 70,
        color: 'white',
        caps: true,
        valign: 'middle',
        maxLines: 1,
      });
      b.rect(0, 180, 300, 56, { fill: 'secondary' });
      b.text(40, 180, 250, 56, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 30,
        valign: 'middle',
        maxLines: 1,
      });
      b.rect(0, 795, 1080, 285, { fill: ['white', 0.94] });
      dish(b, 50, 815, 700, 50);
      b.text(50, 1028, 700, 44, {
        role: 'tagline',
        lang: 'es',
        font: 'body',
        weight: 600,
        size: 30,
        color: 'primary',
        maxLines: 1,
      });
      b.text(760, 830, 280, 110, { role: 'price', size: 72, color: 'primary', align: 'right', maxLines: 1 });
      b.logo(860, 960, 180, 90);
    },
  },
  {
    id: 'special-story',
    category: 'special',
    format: 'story',
    name: bi('Especial en historia', 'Special story'),
    ad: { headline: bi('Especial del día', "Today's special") },
    build: (b) => {
      b.bg('accent');
      b.text(60, 120, 960, 110, {
        role: 'headline',
        lang: 'es',
        size: 88,
        color: 'white',
        align: 'center',
        caps: true,
        maxLines: 1,
      });
      b.text(60, 235, 960, 56, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 40,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.85,
      });
      b.photo(90, 340, 900, 900, { shape: 'ellipse', stroke: 'white', strokeW: 16 });
      dish(b, 70, 1290, 940, 70, { color: 'white', align: 'center' });
      b.rect(310, 1680, 460, 140, { fill: 'secondary', r: 70 });
      b.text(310, 1680, 460, 140, {
        role: 'price',
        size: 76,
        align: 'center',
        valign: 'middle',
        maxLines: 1,
      });
      b.logo(440, 40, 200, 70);
    },
  },
  // ───────────── Promo / discount flyer ─────────────
  {
    id: 'promo-flyer',
    category: 'promo',
    format: 'letter',
    name: bi('Volante de promoción', 'Promo flyer'),
    ad: {
      headline: bi('¡Promoción!', 'Special offer!'),
      tagline: bi('Válido de lunes a jueves', 'Monday to Thursday'),
    },
    build: (b) => {
      b.bg('background');
      b.rect(-9, -9, 630, 230, { fill: 'primary' });
      b.text(36, 40, 420, 80, {
        role: 'headline',
        lang: 'es',
        size: 58,
        color: 'white',
        caps: true,
        maxLines: 1,
      });
      b.text(36, 120, 420, 30, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 22,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.logo(450, 40, 126, 80);
      b.photo(36, 240, 540, 330, { shape: 'rounded', r: 14 });
      b.star(500, 560, 80, 66, 16, { fill: 'secondary' });
      b.text(425, 520, 150, 36, {
        role: 'oldPrice',
        font: 'body',
        size: 18,
        align: 'center',
        valign: 'middle',
        maxLines: 1,
        opacity: 0.7,
      });
      b.line(455, 538, 545, 538, 'text', 2);
      b.text(425, 552, 150, 46, { role: 'price', size: 34, align: 'center', valign: 'middle', maxLines: 1 });
      dish(b, 36, 596, 540, 30);
      b.text(36, 722, 540, 22, {
        role: 'tagline',
        lang: 'es',
        font: 'body',
        weight: 600,
        size: 15,
        color: 'primary',
        maxLines: 1,
      });
      b.text(36, 743, 540, 20, {
        role: 'tagline',
        lang: 'en',
        font: 'body',
        size: 12,
        maxLines: 1,
        opacity: 0.75,
      });
    },
  },
  {
    id: 'promo-discount',
    category: 'promo',
    format: 'ig-square',
    name: bi('Descuento con precio tachado', 'Discount with struck-out price'),
    ad: { headline: bi('Descuento', 'Discount') },
    build: (b) => {
      b.bg('secondary');
      b.photo(380, 0, 700, 1080);
      b.rect(0, 0, 520, 1080, { fill: 'primary' });
      b.text(50, 80, 440, 100, {
        role: 'headline',
        lang: 'es',
        size: 76,
        color: 'white',
        caps: true,
        maxLines: 1,
      });
      b.text(50, 180, 440, 50, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        size: 34,
        color: 'white',
        maxLines: 1,
        opacity: 0.8,
      });
      dish(b, 50, 300, 440, 52, { color: 'white' });
      b.text(50, 700, 440, 70, {
        role: 'oldPrice',
        font: 'body',
        size: 48,
        color: 'white',
        maxLines: 1,
        opacity: 0.75,
      });
      b.line(50, 737, 260, 737, 'white', 5);
      b.text(50, 770, 440, 120, { role: 'price', size: 100, color: 'secondary', maxLines: 1 });
      b.logo(50, 950, 200, 90);
    },
  },
  {
    id: 'promo-2x1',
    category: 'promo',
    format: 'ig-square',
    name: bi('2×1', '2 for 1'),
    ad: { headline: bi('2×1', '2 for 1'), tagline: bi('Solo los martes', 'Tuesdays only') },
    build: (b) => {
      b.bg('primary');
      b.photo(0, 340, 1080, 740);
      b.star(870, 250, 190, 160, 18, { fill: 'secondary' });
      b.text(700, 160, 340, 140, {
        role: 'headline',
        lang: 'es',
        size: 130,
        align: 'center',
        valign: 'middle',
        maxLines: 1,
      });
      b.text(700, 290, 340, 50, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 34,
        align: 'center',
        maxLines: 1,
      });
      b.text(50, 60, 620, 100, {
        role: 'dishName',
        lang: 'es',
        size: 66,
        color: 'white',
        maxLines: 2,
        leading: 1.02,
      });
      b.text(50, 200, 620, 44, {
        role: 'dishName',
        lang: 'en',
        font: 'body',
        size: 32,
        color: 'white',
        maxLines: 1,
        opacity: 0.85,
      });
      b.text(50, 250, 620, 40, {
        role: 'description',
        lang: 'es',
        font: 'body',
        size: 28,
        color: 'white',
        maxLines: 1,
      });
      b.text(50, 290, 620, 34, {
        role: 'description',
        lang: 'en',
        font: 'body',
        size: 22,
        color: 'white',
        maxLines: 1,
        opacity: 0.8,
      });
      b.rect(50, 370, 320, 110, { fill: 'secondary', r: 55 });
      b.text(50, 370, 320, 110, { role: 'price', size: 60, align: 'center', valign: 'middle', maxLines: 1 });
      b.rect(0, 960, 1080, 120, { fill: ['black', 0.55] });
      b.text(50, 960, 700, 120, {
        role: 'tagline',
        lang: 'es',
        size: 44,
        color: 'white',
        valign: 'middle',
        maxLines: 1,
      });
      b.logo(860, 975, 180, 90);
    },
  },
  // ───────────── Combo ─────────────
  {
    id: 'combo-square',
    category: 'combo',
    format: 'ig-square',
    name: bi('Combo', 'Combo'),
    ad: {
      dishName: bi('Combo hamburguesa', 'Burger combo'),
      description: bi('Hamburguesa, papas y bebida', 'Burger, fries and a drink'),
      headline: bi('Combo', 'Combo'),
    },
    build: (b) => {
      b.bg('secondary');
      b.rect(0, 0, 1080, 160, { fill: 'primary' });
      label(b, 50, 30, 600, 100, 'COMBO', 80, 'white');
      b.logo(840, 30, 200, 100);
      b.photo(60, 200, 960, 560, { shape: 'rounded', r: 30 });
      dish(b, 60, 790, 680, 56);
      b.priceBadge(905, 900, 130, { fill: 'primary', color: 'white', star: true });
    },
  },
  {
    id: 'combo-story',
    category: 'combo',
    format: 'story',
    name: bi('Combo en historia', 'Combo story'),
    ad: {
      dishName: bi('Combo familiar', 'Family combo'),
      description: bi(
        '4 piezas de pollo, 2 acompañamientos y bebida grande',
        '4 pieces of chicken, 2 sides and a large drink',
      ),
    },
    build: (b) => {
      b.bg('primary');
      label(b, 60, 100, 960, 140, 'COMBO', 130, 'secondary', 'center');
      b.photo(60, 300, 960, 900, { shape: 'rounded', r: 48 });
      dish(b, 60, 1240, 960, 72, { color: 'white', align: 'center' });
      b.priceBadge(540, 1700, 140, { fill: 'secondary', star: true });
      b.logo(60, 1800, 200, 90);
    },
  },
  // ───────────── New dish ─────────────
  {
    id: 'new-square',
    category: 'new',
    format: 'ig-square',
    name: bi('¡Nuevo!', 'New!'),
    build: (b) => {
      b.photo(0, 0, 1080, 1080);
      b.star(190, 190, 170, 140, 20, { fill: 'secondary' });
      label(b, 60, 130, 260, 70, 'NUEVO', 60, 'text', 'center');
      label(b, 60, 195, 260, 50, 'NEW', 36, 'text', 'center');
      b.rect(0, 820, 1080, 260, { fill: ['primary', 0.94] });
      dish(b, 50, 845, 760, 56, { color: 'white' });
      b.text(800, 860, 240, 110, {
        role: 'price',
        size: 70,
        color: 'secondary',
        align: 'right',
        maxLines: 1,
      });
      b.logo(860, 980, 180, 80);
    },
  },
  {
    id: 'new-story',
    category: 'new',
    format: 'story',
    name: bi('Nuevo en historia', 'New dish story'),
    build: (b) => {
      b.bg('background');
      label(b, 60, 90, 960, 150, 'NUEVO · NEW', 110, 'primary', 'center');
      b.photo(60, 280, 960, 1080, { shape: 'rounded', r: 40 });
      dish(b, 60, 1400, 960, 74, { align: 'center' });
      b.rect(310, 1730, 460, 130, { fill: 'primary', r: 65 });
      b.text(310, 1730, 460, 130, {
        role: 'price',
        size: 72,
        color: 'white',
        align: 'center',
        valign: 'middle',
        maxLines: 1,
      });
      b.logo(60, 1760, 200, 80);
    },
  },
  // ───────────── Happy hour ─────────────
  {
    id: 'happy-square',
    category: 'happy',
    format: 'ig-square',
    name: bi('Happy hour', 'Happy hour'),
    ad: {
      dishName: bi('Limonada de fresa', 'Strawberry lemonade'),
      description: bi('De 4 a 7 p. m.', 'From 4 to 7 pm'),
      headline: bi('Hora feliz', 'Happy hour'),
      tagline: bi('Todos los viernes', 'Every Friday'),
    },
    build: (b) => {
      b.bg('accent');
      b.text(220, 50, 640, 120, {
        role: 'headline',
        lang: 'es',
        size: 100,
        color: 'white',
        caps: true,
        align: 'center',
        maxLines: 1,
      });
      b.text(220, 170, 640, 56, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 40,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.85,
      });
      b.photo(270, 260, 540, 540, { shape: 'ellipse', stroke: 'secondary', strokeW: 14 });
      dish(b, 60, 830, 680, 50, { color: 'white' });
      b.priceBadge(905, 900, 120, { fill: 'secondary' });
      b.logo(30, 40, 170, 90);
    },
  },
  {
    id: 'happy-story',
    category: 'happy',
    format: 'story',
    name: bi('Happy hour en historia', 'Happy hour story'),
    ad: {
      dishName: bi('Cerveza artesanal', 'Craft beer'),
      description: bi('2 por Q 40 de 5 a 8 p. m.', '2 for Q 40 from 5 to 8 pm'),
      headline: bi('Hora feliz', 'Happy hour'),
      tagline: bi('Jueves y viernes', 'Thursdays and Fridays'),
    },
    build: (b) => {
      b.bg('#1d1b2e');
      b.ellipse(540, 960, 600, 600, { fill: ['accent', 0.35] });
      b.text(60, 120, 960, 140, {
        role: 'headline',
        lang: 'es',
        size: 120,
        color: 'secondary',
        align: 'center',
        caps: true,
        maxLines: 1,
      });
      b.text(60, 265, 960, 60, {
        role: 'headline',
        lang: 'en',
        font: 'body',
        weight: 600,
        size: 44,
        color: 'white',
        align: 'center',
        maxLines: 1,
      });
      b.photo(140, 380, 800, 1000, { shape: 'rounded', r: 400 });
      b.priceBadge(880, 1290, 120, { fill: 'secondary' });
      dish(b, 60, 1420, 960, 66, { color: 'white', align: 'center' });
      b.text(60, 1720, 960, 60, {
        role: 'tagline',
        lang: 'es',
        size: 44,
        color: 'secondary',
        align: 'center',
        maxLines: 1,
      });
      b.text(60, 1782, 960, 44, {
        role: 'tagline',
        lang: 'en',
        font: 'body',
        size: 30,
        color: 'white',
        align: 'center',
        maxLines: 1,
        opacity: 0.8,
      });
      b.logo(440, 1840, 200, 70);
    },
  },
];

/** The M14 set, marked 'classic' (hidden in the picker). */
export const CLASSIC_TEMPLATES: TemplateDef[] = CLASSIC.map((t) => ({ ...t, family: 'classic' as const }));
